import React, { createContext, lazy, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import ReactQuill from 'react-quill';

import './LoanDetails.scss';
import checkClosedIcon from 'assets/images/check_closed.svg';

import { Loan, Task, TaskState } from 'gql/graphql';
import { OpenTaskContext } from 'scenes/Private/LoanList/LoanList';
import { useLoanDetails } from './useLoanDetails';
import { RulesTasksCards } from './RulesTask.constanst';
import { useGetLoanClosedByLoan, useGetLoanDocByLoan } from 'services/loan/querys';
import LoanDocuments from './LoanDocuments/LoanDocuments';
import { BorrowerCard } from './BorrowerCard/BorrowerCard';
import LoanProgress from './Progress/Progress';
import { useCreateLoanClosed } from 'services/loan/mutations';
import { useGetUserLoggin } from 'services/user/querys';
import { formatterCurrency } from 'utils/formatters/formatter';
import { LoanOption } from './LoanOption/LoanOption';
import AnnouncementBasic from 'components/Atoms/Announcements/AnnouncementBasic';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import { contactData, contentBoxes } from './utils/transformData';
import { BorrowerTasks } from './BorrowerTasks/BorrowerTasks';
import { ContentBox, Item } from './ContentBox/ContentBox';

interface ILoanDetailsProps {
  onClose: () => void;
  onLoanClosed: Function;
}

export const LoanContext = createContext<Loan | {}>({});
export const TasksContext = createContext<Task[] | []>([]);
export const SelectLoanDocumentContext = createContext<{ loanDocKey: string; setLoanDocKey: any } | {}>({});

const LoanDetails: React.FC<ILoanDetailsProps> = ({ onClose, onLoanClosed }) => {

  const { setTaskKey }: any = useContext(OpenTaskContext);

  const { tasks, loanSelected } = useLoanDetails();
  const taskAddDocs = tasks?.find((item: any) => item.key === 'add-docs');
  
   const { data: anotherDocs, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: {
      findResponsesByTask: { taskId: taskAddDocs?._id }
    },
    pause: true
  });

  const { execute: createLoanClosed } = useCreateLoanClosed();
  const { data: user } = useGetUserLoggin();
  const { data: loanClosed, reexecute: getLoanClosedByLoan } = useGetLoanClosedByLoan({ variables: { loanId: loanSelected?._id } });
  const { data: loanDocs, reexecute: getLoanDocByLoan } = useGetLoanDocByLoan({ variables: { loanId: loanSelected?._id } });
  const [allDocumentsCompleted, setAllDocumentsCompleted] = useState(false);

  useEffect(() => {
    if (tasks && tasks.length > 0)
      getTaskResponses({ requestPolicy: 'network-only' });
  }, [tasks])
  
  useEffect(() => {
    if (loanSelected?._id) {
      getLoanClosedByLoan({ requestPolicy: 'network-only' });
      getLoanDocByLoan({ requestPolicy: 'network-only' });
    }
  }, [loanSelected?._id]);

  const isLoanClosed = Boolean(loanClosed?.[0]?.state === 'closed');

  const [progressData, setProgressData] = useState();
  const [phaseSelect, setPhaseSelect] = useState<string>('approval');
  const [loanDocSelect, setLoanDocSelect] = useState<string>();

  const getTasks = () => {
    // RulesTasksCards es un singleton a nivel de módulo. NO se debe mutar aquí: si lo hacemos,
    // el estado de las tasks de un loan (p.ej. una card Approved) se filtra al siguiente loan
    // abierto en la misma sesión. Construimos una copia fresca por-loan en cada llamada.
    const baseCards = RulesTasksCards.map((card) => ({ ...card, data: undefined, state: undefined }));

    // Cards dinámicas (NewTask creadas por processing, sin key estática) SOLO de este loan.
    const dynamicCards = (tasks?.filter((task: any) => !task.key) || []).map((newTask: any) => ({
      key: newTask._id,
      modalKey: newTask._id,
      name: newTask.name,
      data: newTask,
      phaseProgress: newTask?.phase,
      state: newTask?.state
    }));

    const workingCards: any[] = [...baseCards, ...dynamicCards];

    for (const rules of workingCards) {
      const findTask = tasks?.find((item: any) => item?.key === rules.key);
      const findTaskNew = tasks?.find((item: any) => item?._id === rules.key);
      if (findTask) {
        rules.data = findTask as Task;
        rules.state = findTask?.state;
      }
      if (findTaskNew) {
        rules.data = findTaskNew as Task;
        rules.state = findTaskNew?.state;
      }
    }

    return workingCards.filter((item) => !item.data?.hidden && item.data);
  }

  const allTasksAreApproved = () => {
    // add filter to not include the tasks hidden (deleted from processing team)
    return tasks?.filter((task: any) => task.key !== 'add-docs' && !task?.hidden)?.every((task: any) => task.state === TaskState.Approved);
  }

  const isLoanReadyToClose = () => {
    const loanProgressComplete = loanSelected?.progress?.every((item: any) => item?.stateName === 'COMPLETE')
    return allTasksAreApproved() && allDocumentsCompleted && loanProgressComplete;
  }

  const handleCreateLoanClosed = async () => {
    await createLoanClosed({
      _id: loanClosed?.[0]?._id,
      loan: loanSelected._id,
      state: 'closed',
      user: user?._id || '',
    });
    onClose();
    onLoanClosed({ requestPolicy: 'network-only' });
  }

  if (!loanSelected)
    return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    date.setMinutes(date.getMinutes() + offset);

    const optionsMonthYear = { year: 'numeric', month: 'short' };
    // @ts-ignore
    const monthYear = date.toLocaleDateString('en-US', optionsMonthYear);

    const optionsDay = { day: 'numeric' };
    // @ts-ignore
    const day = date.toLocaleDateString('en-US', optionsDay);

    return { monthYear, day };
  };
  const closingDate = loanSelected?.salesforceData?.closingDate ? formatDate(loanSelected?.salesforceData?.closingDate) : null;
  const date = loanSelected?.salesforceData?.closingDate

  // @ts-ignore
  const momentDate = date ? moment(date) : date

  const closingScheduled = loanSelected?.salesforceData?.closingScheduled;

  const loanOptions = {
    rate: loanSelected?.salesforceData?.selectedOption?.interestRate ? loanSelected?.salesforceData?.selectedOption?.interestRate.toFixed(3) : loanSelected?.loanData?.selectedOption?.rate,
    loanTerm: loanSelected?.salesforceData?.selectedOption?.termInMonths ? `${loanSelected?.salesforceData?.selectedOption?.termInMonths} months` : loanSelected?.loanData?.selectedOption?.loanTerm,
    monthlyPayment: loanSelected?.salesforceData?.selectedOption?.monthlyPayment ? formatterCurrency(loanSelected?.salesforceData?.selectedOption?.monthlyPayment) : formatterCurrency(loanSelected?.loanData?.selectedOption?.monthlyPayment),
    totalLoanAmount: loanSelected?.salesforceData?.selectedOption?.totalLoanAmount ? formatterCurrency(loanSelected?.salesforceData?.selectedOption?.totalLoanAmount) : formatterCurrency(loanSelected?.loanData?.selectedOption?.totalLoanAmount),
    ltv: loanSelected?.salesforceData?.selectedOption?.aivLTV ? loanSelected?.salesforceData?.selectedOption?.aivLTV / 100 : loanSelected?.loanData?.selectedOption?.ltv,
  }

  const ltv = loanOptions.ltv;

  const additionalDocumentsContent: Item[] = [];

  if (anotherDocs) {
    anotherDocs.forEach((anotherDoc: any) => {
      const file = anotherDoc.files[0];
      additionalDocumentsContent.push({
        field: anotherDoc.label,
        value: <span onClick={() => window.open(`${file.url}?preview=true&app=processing`, '_blank')}>{"View file ->"}</span>
      });
    });
  }

  return (
    <LoanContext.Provider value={loanSelected as Loan}>
      <TasksContext.Provider value={tasks as Task[]}>
        <SelectLoanDocumentContext.Provider value={{ loanDocKey: loanDocSelect, setLoanDocKey: setLoanDocSelect }}>
          <div className='loan-details'>
            <div className='loan-details-top-bar'>
              <div className='loan-info'>
                <span className='loan-borrower-name'>{`${loanSelected.owner.name + ' ' + loanSelected.owner.lastname}`} | </span>
                <span className='loan-address'>{loanSelected.name}</span>
              </div>
              <div className='close-button-container' onClick={onClose}>
                CLOSE X
              </div>
            </div>

            <div className='loan-details-first-row'>
              <div className='loan-progress'>
                {!isLoanClosed ?
                  <div className='loan-info'>
                    <span className='loan-status'>UNDERWRITING | </span>
                    <span className='loan-closing-date'>
                      {
                        !closingScheduled ?
                          "Est. Closing:"
                          :
                          "Sch. Closing:"
                      }
                      {
                        date ?
                          <b>
                            {
                              //@ts-ignore
                              momentDate.format('MM/DD/YY')
                            }
                          </b>
                          :
                          <b>
                            {' '}
                            PENDING
                          </b>
                      }
                    </span>
                  </div>
                  :
                  <AnnouncementBasic
                    className='closed-loan mb-10'
                    icon={checkClosedIcon}
                    text={
                      <p>This loan was closed on {' '}
                        <span>{new Date(loanClosed?.[0]?.createdAt).toLocaleDateString()}</span>.
                      </p>
                    }
                  />
                }
                <div className='loan-address'>
                  {loanSelected.name}
                </div>
                <div className='loan-progress-section'>
                  <div className='loan-progress-items-container'>
                    <div className='loan-progress-item-caption'>
                      Loan Progress
                    </div>
                    <LoanProgress
                      loan={loanSelected}
                      phaseSelect={phaseSelect}
                      rulesTasksCards={getTasks()}
                      handlerSetPhaseSelect={setPhaseSelect}
                      handlerSetTaskOpenModal={setTaskKey}
                      handlerSetProgressData={setProgressData}
                    />
                  </div>
                </div>

                <div className='loan-values-summary'>
                  {loanSelected?.type}
                  {loanSelected?.purpose ? (' | ' + loanSelected?.purpose) : ''}
                  {ltv ? (' | ' + ltv * 100 + '% loan amount') : ''}
                </div>
                <div className='loan-values-summary'>
                  {"Loan Number: " + loanSelected?.salesforceData?.loanNumber}
                  {' '}
                  <span onClick={() => { window.open(loanSelected?.salesforceData?.loanURL, '_blank'); }}>{"View in Jungo -->"}</span>
                </div>
              </div>
              <div className='placeholder'></div>
              {loanSelected.owner &&
                <BorrowerCard {...loanSelected.owner} />
              }
            </div>

            <div className='loan-details-main'>
              <div className='left-column'>
                <div className='loan-summary'>
                  <div className='closing'>
                    {
                      closingDate ?
                        <>
                          {
                            closingScheduled ?
                              <div className='label'>Sch. closing</div>
                              :
                              <div className='label'>Est. closing</div>
                          }
                          <div className='calendar'>
                            <div className='date'>
                              {closingDate.monthYear}
                            </div>
                            <div className='day'>
                              {closingDate.day}
                            </div>
                          </div>
                          {
                            closingScheduled ?
                              <div className='caption'>Closing <b>IS</b> scheduled</div>
                              :
                              <div className='caption'>Closing <b>NOT</b> scheduled</div>
                          }
                        </>
                        :
                        <>
                          <div className='label'>Est. closing</div>
                          <div className='calendar disabled'>
                            <div className='date'>
                              PENDING
                            </div>
                            <div className='day'>
                              00
                            </div>
                          </div>
                          <div className='caption'>Closing <b>NOT</b> scheduled</div>
                        </>
                    }
                  </div>
                  <ContentBox {...contactData(loanSelected.salesforceData)} />
                </div>
                <LoanOption {...loanOptions} />
                {contentBoxes(loanSelected).map((box) => (
                  <ContentBox
                    key={box.label}
                    label={box.label}
                    content={box.content}
                  />
                ))}
                <div className='content-box'>
                  <div className='label'>
                    Notes
                  </div>
                  <div className='content'>
                    <ReactQuill
                      value={loanSelected?.salesforceData?.notes || ''}
                      readOnly={true}
                      theme={'bubble'}
                      style={{
                        display: loanSelected?.salesforceData?.notes ? 'block' : 'none'
                      }}
                    />
                  </div>
                </div>
                {anotherDocs && 
                  <ContentBox
                    label={'Additional documents'}
                    content={additionalDocumentsContent}
                  />
                }
              </div>
              <div className='right-column'>
                <BorrowerTasks />
                {tasks &&
                  <LoanDocuments
                    tasks={tasks as Task[]}
                    handlerOpenTask={setTaskKey}
                    setAllDocsComplete={setAllDocumentsCompleted}
                  />
                }
                {!isLoanClosed && isLoanReadyToClose() ? (
                  <div className='action-button' onClick={() => handleCreateLoanClosed()}>
                    Close loan
                  </div>
                ) : (
                  <div className='approve-loan'>
                    Close loan
                  </div>
                )
                }
              </div>
            </div>
          </div>
        </SelectLoanDocumentContext.Provider>
      </TasksContext.Provider>
    </LoanContext.Provider>
  );
};

export default LoanDetails;